import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Input, Select, Button, message, Tree, Spin, Empty, Modal, Form, Popconfirm, Space, TreeSelect, Card, Alert, Tag, Pagination, Drawer, Checkbox } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, CopyOutlined } from '@ant-design/icons';
import { useSuite } from '../../../context/SuiteContext';
import request from '../../../utils/request';
import { getEmployeesList, activeAccount, batchActiveAccount, editEmployeeRemark, syncActiveEmployee } from '../../../api/employee';
import { copyToClipboard } from '../../../utils/clipboard';
import './EmployeeList.css';
import WWOpenData from '../../../components/WWOpenData';

const { Option } = Select;
const { DirectoryTree } = Tree;

const EmployeeList = ({ visible, onClose, corpRecord: propCorpRecord }) => {
    const { suiteId } = useSuite();
    const [loading, setLoading] = useState(false);

    const [corpList, setCorpList] = useState([]);
    const [selectedCorpId, setSelectedCorpId] = useState(propCorpRecord?.corp_id);
    
    // 试用期相关状态
    const [isTrialPeriod, setIsTrialPeriod] = useState(false);
    const [trialEndDate, setTrialEndDate] = useState('');

    const [employeeList, setEmployeeList] = useState([]);

    // Pagination & Filters
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [activeStatus, setActiveStatus] = useState(undefined);
    const [userName, setUserName] = useState('');

    // Group Logic Removed


    // Group Logic Removed
    // const [groupTree, setGroupTree] = useState([]);
    // const [rawGroupTree, setRawGroupTree] = useState([]);
    // const [selectedGroupId, setSelectedGroupId] = useState(undefined);


    // Batch Edit States
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    // const [batchEditModalVisible, setBatchEditModalVisible] = useState(false);

    // const [targetGroupId, setTargetGroupId] = useState(undefined);

    // Edit Remark States
    const [editRemarkModalVisible, setEditRemarkModalVisible] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [remarkValue, setRemarkValue] = useState('');

    // WeCom SDK initialized flag
    const [wecomSdkReady, setWecomSdkReady] = useState(false);

    const location = useLocation();

    // 检查试用期
    const checkTrialPeriod = (licenseCheckTime) => {
        if (!licenseCheckTime || licenseCheckTime === 0) {
            setIsTrialPeriod(false);
            return;
        }
        
        const now = Math.floor(Date.now() / 1000); // 当前时间戳（秒）
        const sevenDaysInSeconds = 7 * 24 * 60 * 60; // 7天的秒数
        const operateThreshold = licenseCheckTime - sevenDaysInSeconds; // 提前7天可操作
        const isInTrial = now <= operateThreshold;
        setIsTrialPeriod(isInTrial);
        
        if (isInTrial) {
            const date = new Date(licenseCheckTime * 1000);
            const formattedDate = date.getFullYear() + '-' + 
                                  String(date.getMonth() + 1).padStart(2, '0') + '-' + 
                                  String(date.getDate()).padStart(2, '0');
            setTrialEndDate(formattedDate);
        }
    };

    const getCorpList = async () => {
        try {
            const res = await request.get('/corp/get_list', {
                params: {
                    page: 1,
                    page_size: 1000,
                    suite_id: suiteId
                }
            });
            if (res.code === 0 && res.data && res.data.list) {
                setCorpList(res.data.list);
                // 如果有propCorpRecord，检查其试用期
                if (propCorpRecord && propCorpRecord.corp_id) {
                    checkTrialPeriod(propCorpRecord.license_check_time);
                }
            }
        } catch (error) {
            console.error('Fetch corp list failed:', error);
        }
    };

    // Update selectedCorpId when prop changes
    useEffect(() => {
        if (propCorpRecord && propCorpRecord.corp_id) {
            setSelectedCorpId(propCorpRecord.corp_id);
            checkTrialPeriod(propCorpRecord.license_check_time);
        }
    }, [propCorpRecord]);

    // Fetch Corp List - Only if NOT in embedded mode (no propCorpRecord)
    // If propCorpRecord is present, we assume we are just managing that corp.
    useEffect(() => {
        if (suiteId && !propCorpRecord) {
            getCorpList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suiteId, propCorpRecord]);
    useEffect(() => {
        if (visible && suiteId && selectedCorpId) {
            setCurrentPage(1);
            fetchEmployees(1, pageSize, undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, suiteId, selectedCorpId]);

    const fetchGroupTree = async () => {
        // Removed
    };

    // Fetch Employees
    const fetchEmployees = async (page, size, groupId) => {
        if (!suiteId || !selectedCorpId) return;

        setLoading(true);
        try {
            const params = {
                suite_id: suiteId,
                corp_id: selectedCorpId,
                page: page,
                pageSize: size,
                code_status: activeStatus
            };
            if (groupId) {
                params.group_id = groupId;
            }
            if (userName && userName.trim()) {
                params.user_name = userName.trim();
            }

            const res = await getEmployeesList(params);
            if (res.code === 0 && res.data) {
                setEmployeeList(res.data.list || []);
                setTotal(res.data.total || 0);
                // API doesn't return page/pageSize, use our parameters
                setCurrentPage(page);
                setPageSize(size);
            } else {
                message.error(res.message || '获取员工列表失败');
            }
        } catch (error) {
            console.error('Fetch employees failed:', error);
            message.error(error.message || '获取员工列表失败');
        } finally {
            setLoading(false);
        }
    };

    // Handlers
    const handleCorpChange = (val) => {
        setSelectedCorpId(val);
    };

    const handleGroupSelect = (selectedKeys) => {
        // Removed
    };

    // Group Management Handlers Removed
    // handleAddGroupClick, handleCreateGroup, handleDeleteGroup, handleEditGroupClick, handleUpdateGroup
    // handleBatchEditClick, handleBatchEditConfirm
    // all removed
    const handleBatchEditClick = () => { }; // Placeholder to avoid breaking references if any


    // Active Account Handler
    const handleActiveAccount = async (record) => {
        try {
            const res = await activeAccount({
                corp_id: selectedCorpId,
                suite_id: suiteId,
                userid: record.userid
            });

            if (res.code === 0) {
                message.success('激活账号成功');
                // Refresh employee list
                fetchEmployees(currentPage, pageSize, undefined);
            } else {
                message.error(res.message || '激活账号失败');
            }
        } catch (error) {
            console.error('Active account failed:', error);
            message.error(error.message || '激活账号失败');
        }
    };

    // Edit Remark Handlers
    const handleEditRemarkClick = (record) => {
        setSelectedEmployee(record);
        setRemarkValue(record.remark || '');
        setEditRemarkModalVisible(true);
    };

    const handleSaveRemark = async () => {
        try {
            const res = await editEmployeeRemark({
                corp_id: selectedCorpId,
                suite_id: suiteId,
                userid: selectedEmployee.userid,
                remark: remarkValue
            });

            message.success('修改备注成功');
            setEditRemarkModalVisible(false);
            setSelectedEmployee(null);
            setRemarkValue('');
            // Refresh employee list
            fetchEmployees(currentPage, pageSize, undefined);
        } catch (error) {
            console.error('Edit remark failed:', error);
            message.error(error.message || '修改备注失败');
        }
    };

    const handleSyncActive = async () => {
        if (!selectedCorpId || !suiteId) {
            message.warning('请先选择企业');
            return;
        }
        try {
            const res = await syncActiveEmployee({
                corp_id: selectedCorpId,
                suite_id: suiteId
            });
            if (res.code === 0) {
                message.success('更新成功');
                handleSearch();
            } else {
                message.error(res.message || '更新失败');
            }
        } catch (error) {
            console.error('Sync active failed:', error);
            message.error('更新失败');
        }
    };

    const handleBatchActive = async () => {
        if (selectedRowKeys.length === 0) {
            message.error('请选择未激活互通账号的员工');
            return;
        }

        try {
            const res = await batchActiveAccount({
                corp_id: selectedCorpId,
                suite_id: suiteId,
                u_ids: selectedRowKeys
            });

            if (res.code === 0) {
                message.success('批量激活成功');
                setSelectedRowKeys([]);
                fetchEmployees(currentPage, pageSize, undefined);
            } else {
                message.error(res.message || '批量激活失败');
            }
        } catch (error) {
            console.error('Batch active failed:', error);
            message.error(error.message || '批量激活失败');
        }
    };


    const handleSearch = () => {
        setCurrentPage(1);
        fetchEmployees(1, pageSize, undefined);
    };

    const handleReset = () => {
        setActiveStatus(undefined);
        setUserName('');
        setCurrentPage(1);
        // Need to pass undefined for status to clear it effectively in the fetch
        // But state update is async.
        // We can just call fetchEmployees with explicit undefined
        // But fetchEmployees uses state `activeStatus`.
        // So we need to wait or pass it as arg.
        // Let's modify fetchEmployees to accept overrides or just rely on effect? 
        // No, effect is not set for filters.
        // Let's manually trigger fetch with cleared params.

        if (!suiteId || !selectedCorpId) return;
        setLoading(true);
        const params = {
            suite_id: suiteId,
            corp_id: selectedCorpId,
            page: 1,
            pageSize: pageSize,
            // group_id: selectedGroupId // Keep group selection? Usually reset clears filters but keeps context.
            // Let's keep group selection.
        };
        // if (selectedGroupId) params.group_id = selectedGroupId;

        getEmployeesList(params).then(res => {
            if (res.code === 0 && res.data) {
                setEmployeeList(res.data.list || []);
                setTotal(res.data.total || 0);
                // API doesn't return page/pageSize, use our values
                setCurrentPage(1);
                // pageSize already set in state
            }
        }).finally(() => setLoading(false));
    };

    const onPageChange = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
        fetchEmployees(page, size, undefined);
    };

    const columns = [
        {
            title: '员工信息',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => {
                return (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>{name || '-'}</span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center', gap: 4 }}>
                            {record.userid || '-'}
                            {record.userid && (
                                <CopyOutlined
                                    style={{ marginLeft: 4, cursor: 'pointer', color: '#1890ff' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(record.userid);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            title: '所属部门',
            dataIndex: 'department_names',
            key: 'department_names',
            render: (names) => {
                if (!names) return '-';
                const deptList = names.split(',').filter(d => d.trim());
                if (deptList.length === 0) return '-';
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                        {deptList.map((dept, index) => (
                            <Tag key={index} color="cyan">
                                {dept.trim()}
                            </Tag>
                        ))}
                    </div>
                );
            }
        },
        {
            title: '员工备注',
            dataIndex: 'remark',
            key: 'remark',
            render: (remark) => remark || '-'
        },
        {
            title: '员工状态',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const statusMap = {
                    1: { text: '正常', color: 'green' },
                    2: { text: '已禁用', color: 'red' },
                    4: { text: '未认证', color: 'orange' },
                    5: { text: '退出企业', color: 'gray' }
                };
                const config = statusMap[status] || { text: '未知', color: 'default' };
                return <Tag color={config.color}>{config.text}</Tag>;
            }
        },
        {
            title: '互通账号状态',
            key: 'active_status',
            render: (_, record) => {
                if (!record.active_code) {
                    return <Tag color="default">未激活</Tag>;
                }

                const statusMap = {
                    2: { text: '已激活', color: 'green' },
                    3: { text: '已过期', color: 'red' },
                    4: { text: '待转移', color: 'orange' },
                    5: { text: '已激活', color: 'green' }, //真实状态是续期
                    6: { text: '分配下游', color: 'blue' }
                };

                const config = statusMap[record.code_status] || { text: '未知', color: 'default' };
                return <Tag color={config.color}>{config.text}</Tag>;
            }
        },
        {
            title: '互通有效期',
            dataIndex: 'code_expire_time',
            key: 'code_expire_time',
            render: (time) => {
                if (!time || time === 0) return '-';
                // Assuming timestamp is in seconds based on typical WeChat API usage, but check length if uncertain.
                // Usually Go returns seconds. JS Date needs ms.
                const date = new Date(time * 1000);
                return date.toLocaleString();
            }
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <span className="action-link" onClick={() => handleEditRemarkClick(record)}>修改备注</span>
                    {!record.active_code && record.status !== 4 && (
                        <Popconfirm
                            title="确定激活互通账号？"
                            onConfirm={() => handleActiveAccount(record)}
                            disabled={isTrialPeriod}
                        >
                            <span 
                                className={`action-link ${isTrialPeriod ? 'disabled' : ''}`}
                                style={{
                                    opacity: isTrialPeriod ? 0.5 : 1,
                                    cursor: isTrialPeriod ? 'not-allowed' : 'pointer',
                                    pointerEvents: isTrialPeriod ? 'none' : 'auto'
                                }}
                            >
                                激活互通账号
                            </span>
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <Drawer
            title="成员管理"
            placement="right"
            onClose={onClose}
            open={visible}
            width={1200} // Increased width for full content
            mask={true} // Modal mode since we are embedded
            style={{ position: 'absolute' }}
            extra={
                <Space>
                    <Button 
                        className="orange-button" 
                        onClick={handleBatchActive}
                        disabled={isTrialPeriod}
                    >
                        批量激活互通
                    </Button>
                    <Button 
                        onClick={handleSyncActive}
                        disabled={isTrialPeriod}
                    >
                        更新互通账号状态
                    </Button>
                </Space>
            }
        >
            <div className="employee-list-container" style={{ padding: 0, minHeight: 'auto' }}>
                {/* 试用期提示 */}
                {isTrialPeriod && propCorpRecord && (
                    <Alert
                        message={
                            <span>
                                {propCorpRecord.corp_name || ''}互通账号在试用期内<span style={{ color: 'red', fontWeight: 'bold' }}>（截止到{trialEndDate}）</span>无需购买激活互通账号，可直接使用。为不影响您的后续使用，<span style={{ color: 'red', fontWeight: 'bold' }}>建议您试用结束提前7天下单购买互通账号</span>。
                            </span>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                <div className="employee-content">
                    {/* Left Panel Removed */}

                    <div className="right-panel">
                        <div className="search-area">
                            {!propCorpRecord && (
                                <Select
                                    showSearch
                                    className="search-item"
                                    placeholder="请选择企业"
                                    optionFilterProp="children"
                                    value={selectedCorpId}
                                    onChange={handleCorpChange}
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {corpList.map(corp => (
                                        <Option key={corp.auth_corp_id} value={corp.auth_corp_id}>
                                            {corp.auth_corp_name}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                            <Input
                                className="search-item"
                                placeholder="请输入成员名称"
                                allowClear
                                value={userName}
                                onChange={(e) => setUserName(e.target.value)}
                                onPressEnter={handleSearch}
                            />
                            <Select
                                className="search-item"
                                placeholder="互通账号状态"
                                allowClear
                                value={activeStatus}
                                onChange={setActiveStatus}
                            >
                                <Option value={1}>未激活</Option>
                                <Option value={2}>已激活</Option>
                                <Option value={3}>已过期</Option>
                            </Select>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Button type="primary" className="orange-button" onClick={handleSearch}>查询</Button>
                                <Button onClick={handleReset}>重置</Button>
                            </div>
                        </div>

                        <Table
                            rowSelection={{
                                selectedRowKeys,
                                onChange: setSelectedRowKeys,
                                getCheckboxProps: (record) => ({
                                    disabled: !!record.active_code || record.status === 4,
                                }),
                                columnTitle: (employeeList.length > 0 && employeeList.every(item => item.active_code || item.status === 4)) ?
                                    <Checkbox disabled /> : undefined
                            }}
                            columns={columns}


                            dataSource={employeeList}
                            rowKey="userid"
                            loading={loading}
                            pagination={false}
                        />

                        <div className="pagination-container" style={{ justifyContent: 'flex-end' }}>
                            <Pagination
                                current={currentPage}
                                pageSize={pageSize}
                                total={total}
                                onChange={onPageChange}
                                showSizeChanger
                                showQuickJumper
                                showTotal={total => `共 ${total} 条`}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Remark Modal */}
            <Modal
                title="修改备注"
                open={editRemarkModalVisible}
                onOk={handleSaveRemark}
                onCancel={() => {
                    setEditRemarkModalVisible(false);
                    setSelectedEmployee(null);
                    setRemarkValue('');
                }}
                okText="保存"
                cancelText="取消"
            >
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>员工备注：</div>
                    <Input.TextArea
                        placeholder="请输入员工备注"
                        value={remarkValue}
                        onChange={(e) => setRemarkValue(e.target.value)}
                        rows={4}
                    />
                </div>
            </Modal>
        </Drawer>
    );
};

export default EmployeeList;
